import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaUserGraduate,
  FaFileInvoiceDollar,
  FaPlus,
  FaTrash,
  FaSearch,
  FaFilter,
  FaTimes,
  FaCheckSquare,
  FaSquare,
  FaCalendarAlt,
  FaTag,
  FaEdit,
  FaBolt,
} from "react-icons/fa";
import { toast } from "react-toastify";

export default function AssignStudentFeePage() {
  const [students, setStudents] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [assignedFees, setAssignedFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  // Filters
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  // Bulk Selection
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  // Modal State for Fee Assignment
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    feeStructureId: "",
    month: "April 2026",
    dueDate: new Date().toISOString().split("T")[0],
    customAmount: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [stuRes, structRes, assignedRes] = await Promise.all([
        axios.get(`${server}/students`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${server}/fee-structures`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${server}/student-fees`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setStudents(stuRes.data.data || []);
      setFeeStructures(structRes.data.data || []);
      setAssignedFees(assignedRes.data.data || []);
    } catch (error) {
      toast.error("Failed to load student fee data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  // Derived filter options
  const classList = useMemo(() => {
    const set = new Set();
    students.forEach((s) => s.class && set.add(String(s.class)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [students]);

  const sectionList = useMemo(() => {
    const set = new Set();
    students.forEach((s) => s.section && set.add(String(s.section)));
    return Array.from(set).sort();
  }, [students]);

  // Filtered Students
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchClass = selectedClass === "ALL" || String(s.class) === selectedClass;
      const matchSec = selectedSection === "ALL" || String(s.section) === selectedSection;
      const st = searchTerm.toLowerCase().trim();
      const matchSearch =
        !st ||
        s.studentName?.toLowerCase().includes(st) ||
        s.admissionNumber?.toLowerCase().includes(st) ||
        s.rollNumber?.toLowerCase().includes(st) ||
        s.fatherName?.toLowerCase().includes(st);

      return matchClass && matchSec && matchSearch;
    });
  }, [students, selectedClass, selectedSection, searchTerm]);

  // Toggle selection
  const toggleSelectAll = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map((s) => s.id));
    }
  };

  const toggleSelectStudent = (id) => {
    if (selectedStudentIds.includes(id)) {
      setSelectedStudentIds(selectedStudentIds.filter((stId) => stId !== id));
    } else {
      setSelectedStudentIds([...selectedStudentIds, id]);
    }
  };

  // Submit Fee Assignment
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (selectedStudentIds.length === 0) return toast.error("Please select at least one student!");
    if (!assignForm.feeStructureId) return toast.error("Please select a Fee Structure!");

    try {
      const payload = {
        studentIds: selectedStudentIds,
        feeStructureId: parseInt(assignForm.feeStructureId),
        month: assignForm.month,
        dueDate: assignForm.dueDate,
        customAmount: assignForm.customAmount ? parseFloat(assignForm.customAmount) : null,
      };

      const res = await axios.post(`${server}/student-fees/assign-bulk`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        toast.success(res.data.message || "Fee assigned successfully!");
        setIsAssignModalOpen(false);
        setSelectedStudentIds([]);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign fee");
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaFileInvoiceDollar size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Assign Student Fees</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Bulk assign fee structures, custom tuition rates, and academic term bills</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedStudentIds.length > 0 && (
            <button
              onClick={() => setIsAssignModalOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
            >
              <FaPlus size={11} /> Assign Fee ({selectedStudentIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Compact Filter Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none"
            >
              <option value="ALL">All Classes</option>
              {classList.map((c) => (
                <option key={c} value={c}>
                  Class - {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none"
            >
              <option value="ALL">All Sections</option>
              {sectionList.map((sec) => (
                <option key={sec} value={sec}>
                  Section - {sec}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-6 relative">
            <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
            <input
              type="text"
              placeholder="Search Student Name, Admission No, Father..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            />
          </div>
        </div>
      </div>

      {/* Main Student List with Bulk Select Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="p-3 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-800 flex justify-between items-center text-xs">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-1.5 font-black text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0 ? (
              <FaCheckSquare size={14} />
            ) : (
              <FaSquare size={14} className="text-gray-300 dark:text-slate-600" />
            )}
            Select All ({filteredStudents.length} Students)
          </button>

          <span className="font-bold text-gray-500 dark:text-slate-400">
            Selected: <span className="text-indigo-600 dark:text-indigo-400 font-black">{selectedStudentIds.length}</span>
          </span>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 text-[10px] uppercase font-black tracking-wider">
                <th className="p-2.5 w-10 text-center">Select</th>
                <th className="p-2.5">Student Name</th>
                <th className="p-2.5">Admission No</th>
                <th className="p-2.5">Class & Sec</th>
                <th className="p-2.5">Father's Name</th>
                <th className="p-2.5 text-center">Assigned Bills</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading students registry...
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-400 italic">
                    No students match the current filters.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((st) => {
                  const isSelected = selectedStudentIds.includes(st.id);
                  const studentAssignedCount = assignedFees.filter((af) => af.studentId === st.id).length;

                  return (
                    <tr
                      key={st.id}
                      onClick={() => toggleSelectStudent(st.id)}
                      className={`cursor-pointer transition ${
                        isSelected
                          ? "bg-indigo-50 dark:bg-indigo-950/40"
                          : "hover:bg-gray-50 dark:hover:bg-slate-800/40"
                      }`}
                    >
                      <td className="p-2.5 text-center">
                        {isSelected ? (
                          <FaCheckSquare className="text-indigo-600 dark:text-indigo-400 text-sm mx-auto" />
                        ) : (
                          <FaSquare className="text-gray-300 dark:text-slate-700 text-sm mx-auto" />
                        )}
                      </td>
                      <td className="p-2.5 font-black text-gray-900 dark:text-white flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-[10px] font-black">
                          {(st.studentName || "S").charAt(0).toUpperCase()}
                        </div>
                        {st.studentName}
                      </td>
                      <td className="p-2.5 font-bold text-gray-500 dark:text-slate-400">
                        {st.admissionNumber || "N/A"}
                      </td>
                      <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                        Class {st.class} ({st.section})
                      </td>
                      <td className="p-2.5 text-gray-500 dark:text-slate-400">
                        {st.fatherName || "N/A"}
                      </td>
                      <td className="p-2.5 text-center">
                        <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 font-black px-2 py-0.5 rounded-full text-[10px]">
                          {studentAssignedCount} Bills
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ASSIGN FEE MODAL */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaFileInvoiceDollar className="text-indigo-500" /> Assign Fee Bill ({selectedStudentIds.length} Students)
              </h3>
              <button onClick={() => setIsAssignModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleAssignSubmit} className="space-y-3">
              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Select Fee Head / Structure *
                </label>
                <select
                  required
                  value={assignForm.feeStructureId}
                  onChange={(e) => setAssignForm({ ...assignForm, feeStructureId: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
                >
                  <option value="">-- Choose Fee Structure --</option>
                  {feeStructures.map((fs) => (
                    <option key={fs.id} value={fs.id}>
                      {fs.title} - ₹{(fs.amount || 0).toLocaleString()} ({fs.frequency})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Target Academic Month *
                </label>
                <select
                  value={assignForm.month}
                  onChange={(e) => setAssignForm({ ...assignForm, month: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
                >
                  {["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"].map((m) => (
                    <option key={m} value={`${m} 2026`}>
                      {m} 2026
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Payment Due Date *
                </label>
                <input
                  type="date"
                  required
                  value={assignForm.dueDate}
                  onChange={(e) => setAssignForm({ ...assignForm, dueDate: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                  Override Amount (Optional)
                </label>
                <input
                  type="number"
                  placeholder="Leave empty to use Fee Structure default"
                  value={assignForm.customAmount}
                  onChange={(e) => setAssignForm({ ...assignForm, customAmount: e.target.value })}
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAssignModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 text-white text-xs font-black rounded-xl hover:bg-indigo-700 transition shadow-md"
                >
                  Assign Fee Bills Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
